import type { ComponentProps } from "react"

import exploreDark from "@/assets/screenshots/explore-desktop-dark.png"
import exploreLight from "@/assets/screenshots/explore-desktop.png"
import { Screenshot } from "@/components/marketing/elements/screenshot"

type ShotName = "explore" | "features" | "scenarios" | "start-in-app" | "integrations"

const SHOTS: Record<ShotName, { alt: string; desktop: string; desktopDark: string }> = {
  // Imported rather than referenced under /img: the bundler fingerprints the
  // filename, so a recapture ships on a URL nothing has cached. The four shots
  // below still show the pre-pivot UI and stay in public/ until they are
  // recaptured too.
  explore: {
    alt: "ArtlessDev Explore view showing scenarios by feature with lifecycle and verification badges",
    desktop: exploreLight,
    desktopDark: exploreDark,
  },
  features: {
    alt: "ArtlessDev features list with status counts per product area",
    desktop: "/img/screenshots/features-desktop.png",
    desktopDark: "/img/screenshots/features-desktop-dark.png",
  },
  scenarios: {
    alt: "ArtlessDev scenarios table listing scenarios by feature with their status badges",
    desktop: "/img/screenshots/scenarios-desktop.png",
    desktopDark: "/img/screenshots/scenarios-desktop-dark.png",
  },
  "start-in-app": {
    alt: "ArtlessDev's Start in app dialog for creating a managed project without connecting GitHub",
    desktop: "/img/screenshots/start-in-app-desktop.png",
    desktopDark: "/img/screenshots/start-in-app-desktop-dark.png",
  },
  integrations: {
    alt: "ArtlessDev Integrations page showing MCP server connection settings",
    desktop: "/img/screenshots/integrations-desktop.png",
    desktopDark: "/img/screenshots/integrations-desktop-dark.png",
  },
}

const screenshotImageClassName = "block h-auto max-w-full w-full bg-white/75 dark:hidden"
const screenshotImageDarkClassName = "hidden h-auto max-w-full w-full bg-black/75 dark:block"

export function ResponsiveAppScreenshot({
  shot,
  wallpaper,
  placement,
  className,
}: {
  shot: ShotName
  wallpaper: ComponentProps<typeof Screenshot>["wallpaper"]
  placement: ComponentProps<typeof Screenshot>["placement"]
  className?: string
}) {
  const { alt, desktop, desktopDark } = SHOTS[shot]

  return (
    <Screenshot className={className ?? "rounded-lg"} wallpaper={wallpaper} placement={placement}>
      <img src={desktop} alt={alt} className={screenshotImageClassName} />
      <img src={desktopDark} alt="" aria-hidden className={screenshotImageDarkClassName} />
    </Screenshot>
  )
}
